import type { CSSProperties } from 'react';
import { Link } from 'react-router-dom';
import { apiPost, TENANT_TOKEN_KEY } from '../../../api/client';
import { chatLinkUnlessDeepLink } from './conversationIdFromDeepLink';
import type { OperatorNotificationBrief } from './useOpenOperatorNotifications';

type Props = {
  items: OperatorNotificationBrief[];
  loading: boolean;
  onChanged: () => void;
};

function formatTimeShort(iso: string): string {
  try {
    const d = new Date(iso);
    const diffM = Math.floor((Date.now() - d.getTime()) / 60000);
    if (diffM < 1) return 'ahora';
    if (diffM < 60) return `hace ${diffM} min`;
    const diffH = Math.floor(diffM / 60);
    if (diffH < 24) return `hace ${diffH} h`;
    return d.toLocaleDateString('es-UY', { day: 'numeric', month: 'short' });
  } catch {
    return '';
  }
}

export function ConversationsAlertsStrip({ items, loading, onChanged }: Props) {
  const token = localStorage.getItem(TENANT_TOKEN_KEY);

  const dismiss = async (id: string) => {
    if (!token) return;
    await apiPost(`/api/console/notifications/${id}/dismiss`, {}, token, { showErrorGrowl: true });
    window.dispatchEvent(new Event('va-operator-notifications-updated'));
    onChanged();
  };

  const declineBooking = async (id: string) => {
    if (!token) return;
    if (!window.confirm('¿No reservar? Se libera la seña pendiente y el chat vuelve a automático.')) return;
    await apiPost(`/api/console/notifications/${id}/decline-booking`, {}, token, {
      showErrorGrowl: true,
      successMessage: 'Se liberó la seña y el chat volvió a automático',
    });
    window.dispatchEvent(new Event('va-operator-notifications-updated'));
    onChanged();
  };

  if (loading && items.length === 0) {
    return (
      <div style={{ padding: '8px 14px 0', fontSize: 12, color: '#64748b' }}>Cargando alertas…</div>
    );
  }

  if (items.length === 0) return null;

  return (
    <div style={{ padding: '8px 14px 0', flexShrink: 0 }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 8,
          marginBottom: 6,
        }}
      >
        <span style={{ fontSize: 11, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
          Alertas ({items.length})
        </span>
        <Link to="/app/notifications" style={{ fontSize: 11, color: '#7c3aed', fontWeight: 600, textDecoration: 'none' }}>
          Ver todas
        </Link>
      </div>
      <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
        {items.map((n) => (
          <li
            key={n.id}
            style={{
              borderRadius: 8,
              border: '1px solid rgba(15, 23, 42, 0.08)',
              background: n.backgroundColor || '#f8fafc',
              padding: '8px 10px',
            }}
          >
            <div style={{ fontSize: 12, fontWeight: 600, color: '#0f172a', lineHeight: 1.35 }}>{n.title}</div>
            {n.body ? (
              <div style={{ fontSize: 11, color: '#475569', marginTop: 4, lineHeight: 1.4 }}>{n.body}</div>
            ) : null}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 8,
                marginTop: 8,
                flexWrap: 'wrap',
              }}
            >
              <span style={{ fontSize: 10, color: '#64748b' }}>{formatTimeShort(n.updatedAt)}</span>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <Link to={n.deepLinkPath} style={linkBtn}>
                  Abrir
                </Link>
                {chatLinkUnlessDeepLink(n.deepLinkPath, n.payload) ? (
                  <Link to={chatLinkUnlessDeepLink(n.deepLinkPath, n.payload)!} style={linkBtn}>
                    Ver chat
                  </Link>
                ) : null}
                {n.type === 'calendar.booking_visitor_confirmed' && n.status === 'open' ? (
                  <button type="button" onClick={() => void declineBooking(n.id)} style={textBtn}>
                    No reservar
                  </button>
                ) : null}
                <button type="button" onClick={() => void dismiss(n.id)} style={textBtn}>
                  Descartar
                </button>
              </div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

const linkBtn: CSSProperties = {
  fontSize: 11,
  fontWeight: 600,
  color: '#6d28d9',
  textDecoration: 'none',
  padding: 0,
  border: 'none',
  background: 'none',
  cursor: 'pointer',
  fontFamily: 'inherit',
};

const textBtn: CSSProperties = {
  fontSize: 11,
  border: 'none',
  background: 'transparent',
  color: '#64748b',
  cursor: 'pointer',
  textDecoration: 'underline',
  padding: 0,
  fontFamily: 'inherit',
};
